import { useEffect, useMemo, useState } from 'react';
import {
  listInvestorActivity,
  listInvestorActivityEventTypes,
  listInvestorOrganizations,
} from '../../api/investorApi';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { FilterSelect } from '../../components/ui/FilterSelect';
import { Pagination } from '../../components/ui/Pagination';
import { ActivityTimeline } from './ActivityTimeline';
import { humanize } from './investorLabels';

const inputClass =
  'h-9 rounded-md border border-[#D1D5DB] bg-white px-3 text-sm text-[#1F2933] focus:outline-none focus:ring-2 focus:ring-[#00A6D6]/30';

function toIso(dateValue, endOfDay = false) {
  if (!dateValue) return undefined;
  return endOfDay ? `${dateValue}T23:59:59` : `${dateValue}T00:00:00`;
}

export function InvestorActivityPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [eventType, setEventType] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [eventTypes, setEventTypes] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listInvestorActivityEventTypes(), listInvestorOrganizations({ pageSize: 100 })])
      .then(([types, orgs]) => {
        if (cancelled) return;
        setEventTypes(Array.isArray(types) ? types : []);
        setOrganizations(orgs?.items || []);
      })
      .catch(() => {
        // filters are optional
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const response = await listInvestorActivity({
          page,
          pageSize,
          eventType,
          organizationId,
          dateFrom: toIso(dateFrom),
          dateTo: toIso(dateTo, true),
          q: query,
        });
        if (cancelled) return;
        setItems(response?.items || []);
        setTotal(response?.meta?.total ?? 0);
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Unable to load activity.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [page, pageSize, eventType, organizationId, dateFrom, dateTo, query, reloadToken]);

  const typeOptions = useMemo(
    () => [
      { value: '', label: 'All events' },
      ...eventTypes.map((type) => ({ value: type, label: humanize(type) })),
    ],
    [eventTypes]
  );
  const orgOptions = useMemo(
    () => [
      { value: '', label: 'All organizations' },
      ...organizations
        .filter((org) => org.org_type === 'originator')
        .map((org) => ({ value: org.id, label: org.name })),
    ],
    [organizations]
  );

  return (
    <div className="space-y-4">
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setPage(1);
          setQuery(search.trim());
        }}
      >
        <input
          type="search"
          aria-label="Search activity"
          placeholder="Search message, package or person"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className={`${inputClass} w-64`}
        />
        <FilterSelect
          label="Event"
          ariaLabel="Filter by event type"
          value={eventType}
          onChange={(value) => {
            setPage(1);
            setEventType(value);
          }}
          options={typeOptions}
        />
        <FilterSelect
          label="Originator"
          ariaLabel="Filter by originator"
          value={organizationId}
          onChange={(value) => {
            setPage(1);
            setOrganizationId(value);
          }}
          options={orgOptions}
        />
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#5A5A5A]">
          From
          <input
            type="date"
            aria-label="From date"
            value={dateFrom}
            onChange={(event) => {
              setPage(1);
              setDateFrom(event.target.value);
            }}
            className={inputClass}
          />
        </label>
        <label className="inline-flex items-center gap-2 text-xs font-semibold text-[#5A5A5A]">
          To
          <input
            type="date"
            aria-label="To date"
            value={dateTo}
            onChange={(event) => {
              setPage(1);
              setDateTo(event.target.value);
            }}
            className={inputClass}
          />
        </label>
      </form>

      {error ? <ErrorBanner message={error} onRetry={() => setReloadToken((v) => v + 1)} /> : null}

      <section className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card">
        {loading ? (
          <p className="text-xs text-eorig-muted">Loading activity…</p>
        ) : (
          <ActivityTimeline items={items} emptyMessage="No activity matches these filters." />
        )}
      </section>

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(1);
          setPageSize(size);
        }}
        disabled={loading}
      />
    </div>
  );
}
